import React from 'react';

import autoBind from 'react-autobind';

// import other components within the same project here

import Localization from "../Localization.jsx"

import { Modal, Button, Group, Text } from '@mantine/core';


// ---------------------------------------------------------------------------
// Shared, app-wide confirmation dialog.
//
// This is the single "simple confirmation" component for the whole editor: a
// short message plus Confirm / Cancel buttons. It is mounted once in RootPage
// and driven imperatively from anywhere via the root ref:
//
//   this.props.root.refs.confirmDialog.show(
//     { title, content, confirm, cancel },  // every field optional
//     onConfirm,                             // omit -> no Confirm button
//     onCancel,                              // omit -> no Cancel button
//     onClose,                               // optional: dismissed w/o a button
//   );
//
// Rendered as a compact, centered Mantine modal (phone-style alert) so it stays
// consistent with the rest of the migrated UI. Kept as a class with a string
// ref target so the imperative `show()` API keeps working unchanged.
// ---------------------------------------------------------------------------
class ConfirmDialog extends React.Component {
  constructor(props) {
    super(props);

    // state variables here
    this.state = {
      open: false,
      titleText: Localization.strings.TITLE,
      contentText: Localization.strings.CONTENT,
      confirmText: Localization.strings.OK,
      cancelText: Localization.strings.CANCEL,
      confirmCallback: null,
      cancelCallback: null,
      onCloseCallback: null,
      buttonClicked: false,
    };
    autoBind(this);
  }

  show(dialogText, confirmCallback, cancelCallback, onCloseCallback = null) {
    if (dialogText) {
      let title = (dialogText.title? dialogText.title : Localization.strings.TITLE);
      let content = (dialogText.content? dialogText.content : Localization.strings.CONTENT);
      let confirm = (dialogText.confirm? dialogText.confirm : Localization.strings.OK);
      let cancel = (dialogText.cancel? dialogText.cancel : Localization.strings.CANCEL);
      this.state.titleText = title;
      this.state.contentText = content;
      this.state.confirmText = confirm;
      this.state.cancelText = cancel;
      this.setState({
        titleText: title,
        contentText: content,
        confirmText: confirm,
        cancelText: cancel,
      });
    }
    this.state.confirmCallback = confirmCallback;
    this.state.cancelCallback = cancelCallback;
    this.state.onCloseCallback = onCloseCallback;
    this.state.buttonClicked = false;
    this.state.open = true;
    this.setState({
      confirmCallback: confirmCallback,
      cancelCallback: cancelCallback,
      onCloseCallback: onCloseCallback,
      buttonClicked: false,
      open: true,
    });
  }

  handleConfirmClicked() {
    this.setState({ buttonClicked: true });
    this.state.buttonClicked = true;
    if (this.state.confirmCallback) {
      this.state.confirmCallback();
    }
    this.handleClose();
  }

  handleCancelClicked() {
    this.setState({ buttonClicked: true });
    this.state.buttonClicked = true;
    if (this.state.cancelCallback) {
      this.state.cancelCallback();
    }
    this.handleClose();
  }

  handleClose() {
    // If dialog is being closed without a button click (e.g., clicking outside)
    if (!this.state.buttonClicked && this.state.onCloseCallback) {
      this.state.onCloseCallback();
    }
    this.setState({ open: false, buttonClicked: false });
    this.state.open = false;
    this.state.buttonClicked = false;
  }

  render() {
    return (
      <Modal
        opened={this.state.open}
        onClose={this.handleClose}
        title={<Text fw={600}>{this.state.titleText}</Text>}
        size="sm"
        radius="md"
        centered
        // Sit above every other modal: confirms are often triggered from
        // within other dialogs (e.g. AdminTaskDialog at z-index ~2100).
        zIndex={100000}
        overlayProps={{ backgroundOpacity: 0.45, blur: 1 }}
      >
        <Text style={{ whiteSpace: 'pre-line' }}>
          {this.state.contentText}
        </Text>
        <Group justify="flex-end" gap="sm" mt="lg">
          {this.state.cancelCallback != null && (
            <Button variant="subtle" onClick={this.handleCancelClicked}>
              {this.state.cancelText}
            </Button>
          )}
          {this.state.confirmCallback != null && (
            <Button onClick={this.handleConfirmClicked}>
              {this.state.confirmText}
            </Button>
          )}
        </Group>
      </Modal>
    )
  }
}

export default ConfirmDialog;
